<template>
  <ModalView
    v-model="show"
    :title="confirmingApprovalEmail ? 'Notify applicant?' : (license && !license.isNew ? `Edit License #${license.number}` : 'Add License')"
    :busy="model && model.submitting"
    scrollable
    size="lg"
    :ok-title="`Save ${license && license.isNew ? 'New ' : ''}License`"
    :hide-ok="confirmingApprovalEmail"
    :hide-cancel="confirmingApprovalEmail"
    @ok="commitLicense"
    :disable-ok="!canSubmit">
    <template #buttons>
      <div v-if="confirmingApprovalEmail" class="w-100 d-flex justify-content-end">
        <button type="button" class="btn btn-flat" @click="confirmingApprovalEmail = false">
          Cancel
        </button>
      </div>
      <button v-else-if="!deleting && canDelete" type="button" class="btn btn-flat text-danger" @click="deleting = true">
        <i class="bi-trash" /> Delete
      </button>
    </template>
    <div v-if="confirmingApprovalEmail">
      <p class="mb-3">
        Choose which notifications to send to
        <strong>{{ model.contact?.email || 'the applicant' }}</strong>
        with this license update.
      </p>
      <div class="d-flex flex-column gap-2 mb-4">
        <label
          v-for="rule in pendingEmailRules"
          :key="rule.id"
          class="border rounded p-3 d-flex flex-row align-items-start gap-3"
        >
          <input
            v-model="selectedEmailRuleIds"
            class="form-check-input mt-1"
            type="checkbox"
            :value="rule.id"
          />
          <span>
            <span class="d-block fw-semibold">{{ rule.name }}</span>
            <span class="d-block small text-muted">{{ rule.template?.subject }}</span>
          </span>
        </label>
      </div>
      <div class="license-email-actions d-flex flex-column align-items-center gap-2">
        <button
          type="button"
          class="btn btn-primary"
          :disabled="model && model.submitting"
          @click="commitLicenseWithSelections"
        >
          Save and send selected
        </button>
        <button
          type="button"
          class="btn btn-outline-secondary"
          :disabled="model && model.submitting"
          @click="commitLicenseWithoutNotifications"
        >
          Save without notifications
        </button>
      </div>
    </div>
    <template v-else-if="license && !buildingModel && model">
      <BCollapse :show="!deleting">
        <form @submit.prevent="commitLicense" class="d-flex flex-column">

          <div v-if="error" class="alert alert-danger mb-0" role="alert">
            {{ error.message || String(error) }}
          </div>

          <div class="modal-section">
            <h3>Property</h3>
            <AddressRegistrationForm :model="model" size="md" />
          </div>

          <div class="modal-section">
            <h3>License Term &amp; Status</h3>
            <LicenseIdEditor :model="model" editable @saved="licenseIdSaved" />
            <div>
              <label class="form-label">Date Applied</label>
              <div class="d-flex flex-row align-items-center">
                <DateInput v-model="model.appliedOn" class="form-control flex-grow-1" />
                <button type="button" class="btn btn-flat ms-1" @click="model.appliedOn = todayString()">
                  Today
                </button>
              </div>
            </div>
            <div>
              <label class="form-label">Date Issued</label>
              <div class="d-flex flex-row align-items-center">
                <DateInput v-model="model.issuedOn" class="form-control flex-grow-1" />
                <button type="button" class="btn btn-flat ms-1" @click="model.issuedOn = todayString()">
                  Today
                </button>
              </div>
            </div>
            <div>
              <label class="form-label">Status</label>
              <select class="form-select" v-model="model.status">
                <option disabled>Select one:</option>
                <option v-for="status in license.statusOptions" :key="status.code" :value="status.code">{{ status.label }}</option>
              </select>
            </div>
          </div>

          <div class="modal-section">
            <h3>Emergency Contact</h3>
            <OwnerRegistrationForm :model="model" />
          </div>

          <div v-if="model.hasCustomFields" class="modal-section">
            <h3>Local Requirements</h3>
            <div v-if="!showCustomForm" class="d-flex flex-row align-items-center text-muted small">
              <SpinnerView small class="me-2" />
              Loading local requirements...
            </div>
            <CustomRegistrationForm v-else :model="model" />
          </div>

        </form>
      </BCollapse>
      <BCollapse :show="deleting">
        <div class="p-3">
          <div class="card card-body shadow">
            <label class="fw-bold mb-3">Are you sure you want to delete this license?</label>
            <div class="d-flex flex-row">
              <div class="w-50 pe-1">
                <a class="btn btn-outline-danger w-100" @click="commitDelete">Yes, delete it</a>
              </div>
              <div class="w-50 ps-1">
                <a class="btn btn-outline-dark w-100" @click="deleting = false">No, keep it</a>
              </div>
            </div>
          </div>
        </div>
      </BCollapse>
    </template>
    <template v-else-if="license && buildingModel">
      <div class="d-flex flex-row align-items-center text-muted">
        <SpinnerView small class="me-2" />
        Loading license editor...
      </div>
    </template>
    <template v-else>
      Select a license to edit.
    </template>
  </ModalView>
</template>

<script>
import { todayString } from '@strenforcement/domain/licenseExpiration';
import {
  LEGACY_LICENSE_APPROVAL_EMAIL_RULE_ID,
  getLicenseStatusEmailRulesRequiringConfirmation
} from '@strenforcement/domain/licenseStatusEmail';
import ModalView from "@/components/common/ModalView.vue";
import {License} from "@/models/License";
import BCollapse from "@/components/common/BCollapse.vue";
import AddressRegistrationForm from "@/components/register/AddressRegistrationForm.vue";
import OwnerRegistrationForm from "@/components/register/OwnerRegistrationForm.vue";
import CustomRegistrationForm from "@/components/register/CustomRegistrationForm.vue";
import {LicenseEditorModel} from "@/models/LicenseEditorModel";
import DateInput from "@/components/common/DateInput.vue";
import SpinnerView from "@/components/common/SpinnerView.vue";
import LicenseIdEditor from "@/components/licenses/LicenseIdEditor.vue";
import { showLicenseStatusEmailNotificationToasts } from '@/licenseStatusEmailNotification';
export default {
  components: {
    LicenseIdEditor,
    SpinnerView,
    DateInput,
    CustomRegistrationForm,
    OwnerRegistrationForm,
    AddressRegistrationForm,
    BCollapse,
    ModalView
  },
  emits: ['saved','closed','update:license','license-id-saved'],
  props: {
    license: License
  },
  data() {
    return {
      model: null,
      error: null,
      deleting: false,
      confirmingApprovalEmail: false,
      pendingEmailRules: [],
      selectedEmailRuleIds: [],
      buildingModel: false,
      showCustomForm: false,
      modelBuildVersion: 0,
      profileSequence: 0,
      profilePrefix: null,
      activeProfileTimers: {}
    };
  },
  watch: {
    license: {
      async handler() {
        const version = ++this.modelBuildVersion;
        this.deleting = false;
        this.confirmingApprovalEmail = false;
        this.pendingEmailRules = [];
        this.selectedEmailRuleIds = [];
        this.error = null;
        this.showCustomForm = false;
        if (!this.license) {
          this.endProfileTimer('license -> model');
          this.endProfileTimer('defer before model');
          this.endProfileTimer('build LicenseEditorModel');
          this.endProfileTimer('custom form mount');
          this.buildingModel = false;
          this.model = null;
          return;
        }

        this.beginProfileSession();
        this.startProfileTimer('license -> model');
        this.buildingModel = true;
        this.model = null;

        // Allow the modal to render and open before we do heavier model setup.
        this.startProfileTimer('defer before model');
        await this.$nextTick();
        await new Promise(resolve => setTimeout(resolve, 0));
        this.endProfileTimer('defer before model');

        if (version !== this.modelBuildVersion || !this.license) {
          this.endProfileTimer('license -> model');
          return;
        }

        this.startProfileTimer('build LicenseEditorModel');
        this.model = new LicenseEditorModel(this.license.city, this.license.data, this.license.id);
        this.endProfileTimer('build LicenseEditorModel');
        this.endProfileTimer('license -> model');
        this.buildingModel = false;

        // ModalView reports dismissals, so opening must not depend on a v-model echo.
        this.deferCustomFormMount();
      },
      immediate: true
    }
  },
  computed: {
    canDelete() {
      return false;
    },
    canSubmit() {
      return this.model?.isComplete;
    },
    show: {
      get() { return !!this.license; },
      set(show) {
        if (!show) {
          this.closeModal();
        }
      }
    },
    city() {
      if (this.model) {
        return this.model.city;
      } else {
        return null;
      }
    }
  },
  methods: {
    todayString,
    beginProfileSession() {
      this.endAllProfileTimers();
      const context = this.license?.id ? `license:${this.license.id}` : `new:${this.license?.data?.propertyId || 'unknown'}`;
      this.profileSequence += 1;
      this.profilePrefix = `[EditLicenseModal ${this.profileSequence} ${context}]`;
    },
    profileTimerLabel(name) {
      return this.profilePrefix ? `${this.profilePrefix} ${name}` : null;
    },
    startProfileTimer(name) {
      const label = this.profileTimerLabel(name);
      if (!label || this.activeProfileTimers[label]) {
        return;
      }
      this.activeProfileTimers[label] = true;
      console.time(label);
    },
    endProfileTimer(name) {
      const label = this.profileTimerLabel(name);
      if (!label || !this.activeProfileTimers[label]) {
        return;
      }
      console.timeEnd(label);
      delete this.activeProfileTimers[label];
    },
    endAllProfileTimers() {
      Object.keys(this.activeProfileTimers).forEach(label => {
        console.timeEnd(label);
        delete this.activeProfileTimers[label];
      });
    },
    deferCustomFormMount() {
      this.showCustomForm = false;
      if (!this.model || !this.model.hasCustomFields) {
        this.showCustomForm = true;
        return;
      }
      const version = this.modelBuildVersion;
      this.startProfileTimer('custom form mount');
      setTimeout(() => {
        if (this.show && version === this.modelBuildVersion && this.model?.hasCustomFields) {
          this.showCustomForm = true;
          this.$nextTick(() => {
            this.endProfileTimer('custom form mount');
          });
        } else {
          this.endProfileTimer('custom form mount');
        }
      }, 0);
    },
    closeModal() {
      this.deleting = false;
      this.confirmingApprovalEmail = false;
      this.pendingEmailRules = [];
      this.selectedEmailRuleIds = [];
      this.showCustomForm = false;
      this.buildingModel = false;
      this.modelBuildVersion += 1;
      this.endAllProfileTimers();
      this.model = null;
      this.$emit('update:license', null);
      this.$emit('closed');
    },
    async commitLicense() {
      this.error = null;
      try {
        const confirmationRules = !this.license.isNew
          ? getLicenseStatusEmailRulesRequiringConfirmation({
          rules: this.city.licenseStatusEmailRules,
          previousStatus: this.license.data.status,
          nextStatus: this.model.status,
          statusConfig: this.license.statusOptions
        })
          : [];
        const recipientEmail = String(this.model.contact?.email || '').trim();
        if (confirmationRules.length && recipientEmail) {
          this.pendingEmailRules = confirmationRules;
          this.selectedEmailRuleIds = confirmationRules.map(rule => rule.id);
          this.confirmingApprovalEmail = true;
          return;
        }
        await this.saveLicense();
      } catch (e) {
        this.error = e;
      }
    },
    buildEmailNotificationDecisions({ sendSelected = true } = {}) {
      const selected = new Set(sendSelected ? this.selectedEmailRuleIds : []);
      return Object.fromEntries(this.pendingEmailRules.map(rule => [
        rule.id,
        selected.has(rule.id) ? 'send' : 'skip'
      ]));
    },
    async commitLicenseWithSelections() {
      this.error = null;
      this.confirmingApprovalEmail = false;
      try {
        await this.saveLicense(this.buildEmailNotificationDecisions());
      } catch (e) {
        this.error = e;
      }
    },
    async commitLicenseWithoutNotifications() {
      this.error = null;
      this.confirmingApprovalEmail = false;
      try {
        await this.saveLicense(this.buildEmailNotificationDecisions({ sendSelected: false }));
      } catch (e) {
        this.error = e;
      }
    },
    async saveLicense(emailNotificationDecisions) {
      const approvalNotificationDecision = emailNotificationDecisions?.[
        LEGACY_LICENSE_APPROVAL_EMAIL_RULE_ID
      ];
      const license = await this.model.saveEdits({
        approvalNotificationDecision,
        emailNotificationDecisions
      });
      this.$emit('saved', license);
      showLicenseStatusEmailNotificationToasts(
        this,
        license.emailNotifications?.length
          ? license.emailNotifications
          : (license.approvalNotification ? [license.approvalNotification] : [])
      );

      this.closeModal();
    },
    licenseIdSaved(license) {
      if (this.license?.data && license?.data) {
        this.license.data.licenseId = license.data.licenseId;
      }
      this.$emit('license-id-saved', license);
    }
  }
}
</script>
